<script>
  import { getContext, onMount } from "svelte";
  import { useNavigate, useLocation } from "svelte-navigator";

  import * as yup from "yup";

  import { login } from "svelte-session-manager";

  import { PasswordInput, TextInput } from "paxray-components-svelte";
  import { AuthService } from "@services/index.js";
  import LoadingRipple from "@components/common/suspense/LoadingRipple.svelte";
  import MicrosoftButton from "./MicrosoftButton.svelte";
  import { fetchWrapper } from "lib/util/fetchWrapper.js";

  import Button from "../../Button.svelte";

  // session
  const session = getContext("session");
  const sessionUpdate = getContext("sessionUpdate");

  // url params
  const location = useLocation();
  const navigate = useNavigate();

  let loading = false;
  let token = "";
  let enableSSO = false; // TODO: this should be fetched from the backend

  // services
  const authService = new AuthService();

  // validation
  const schema = yup.object().shape({
    // email: yup.string().email().required(), // TODO: this needs to be enabled. Currently, we are using a string username for login
    email: yup.string().required("Please enter your email address."),
    password: yup.string().required("Please enter your password."),
  });

  let setupUrl = "";
  let errorMessage = "Username/email or password is incorrect.";

  async function submitHandler() {
    isFetching = true;
    await schema.validate(form, { abortEarly: false });
    const successful = await loginUser();

    if (!successful) {
      errors = {
        email: "Invalid credentials.",
        password: "Invalid credentials.",
        password2: "",
      };
    }
    isFetching = false;
  }

  // Variables and local states
  $: form = {
    email: "",
    password: "",
    password2: "",
  };
  $: errors = {
    email: "",
    password: "",
    password2: "",
  };

  let isFetching = false;
  // let isPasswordReset = false

  /**
   * @type {'login' | 'requestReset' | 'passwordReset'|  'passwordResetSuccess'}
   */
  let loginUIState = "login";

  // login function
  async function loginUser() {
    await login(session, form.email, form.password);
    if (session.isValid) {
      form = {
        email: "",
        password: "",
        password2: "",
      };

      if (sessionUpdate) {
        sessionUpdate();
      }
      if ($location.pathname === "/") {
        navigate("/app/", { replace: true });
      } else {
        navigate($location.pathname, { replace: true });
      }
      return true;
    }
    return false;
  }

  const togglePasswordReset = () => {
    let isPasswordReset = loginUIState === "requestReset";
    form.password = "";
    // clean errors if any
    errors = {
      email: "",
      password: "",
      password2: "",
    };
    loginUIState = isPasswordReset ? "login" : "requestReset";
  };

  // Password reset
  const passwordResetSubmitHandler = async () => {
    const resetSchema = yup.object().shape({
      email: yup.string().required("Please enter your email address."),
    });

    const handlePasswordResetEmailValidationAndSending = async () => {
      isFetching = true;
      loading = true;

      try {
        await resetSchema.validate(form, { abortEarly: false });
        await authService
          .requestPasswordReset(form.email)
          .catch(() => {
            // We don't want to show the error message here, because it might be a security issue
            // and we don't want to give the attacker any information about the existence of the email address
            () => {};
          })
          .finally(() => {
            loginUIState = "passwordReset";
          });
      } catch (err) {
        // errors = extractErrors(err); // this will only catch yup errors
      } finally {
        isFetching = false;
        loading = false;
      }
    };

    handlePasswordResetEmailValidationAndSending();
  };

  // change password
  const changePasswordSubmitHandler = async () => {
    const changePasswordSchema = yup.object().shape({
      password: yup
        .string()
        .required("Please enter your new password.")
        .min(8, "Password must be at least 8 characters long."),
      password2: yup
        .string()
        .required("Please repeat your new password.")
        .oneOf([yup.ref("password"), null], "Passwords must match."),
    });
    try {
      await changePasswordSchema.validate(form, { abortEarly: false });
      if (!token) throw new Error("No token found."); // this should never happen
      isFetching = true;
      loading = true;
      await authService.changePassword(token, form.password).then(() => {
        loginUIState = "login";
      });
    } catch (err) {
      if (err?.inner?.length > 0) {
        // errors = extractErrors(err);
      } else {
        throw new Error("Invalid or expired token.");
      }
    } finally {
      isFetching = false;
      loading = false;
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      submitHandler();
    }
  };

  onMount(async () => {
    try {
      const res = await fetchWrapper(`/api/setup-info`);
      const data = await res.json();

      if (!data.setupcomplete) {
        setupUrl = data.setup_url;
      }

      if (setupUrl) return;

      const urlParams = new URLSearchParams($location.search);
      token = urlParams.get("token") ?? "";
      if (!token) {
        return;
      }
      loginUIState = "passwordResetSuccess";
    } catch (err) {
      console.error("Error fetching setup info:", err);
      return;
    }
  });
</script>

<div class="login-module-container">
  {#if setupUrl}
    <div class="setup_incomplete">
      <p>
        The setup process is still in progress. Please complete <b>setup</b> before
        continuing
      </p>
      <Button
        variant="secondary"
        on:click={(document.location = setupUrl)}
        text="Let's get started!"
      />
    </div>
  {:else}
    <h1>
      {#if loginUIState === "login"}
        Sign In
      {:else}
        Reset Password
      {/if}
    </h1>
  {/if}

  {#if !loading}
    <p>
      {#if loginUIState === "requestReset"}
        Please enter email address to reset password.
      {:else if loginUIState === "passwordReset"}
        A mail to reset your password will be sent to <br />
        {form.email}. This may take a few minutes.
      {:else if loginUIState === "passwordResetSuccess"}
        Please set a new password.
      {/if}
    </p>

    <form on:submit|preventDefault={submitHandler}>
      {#if loginUIState !== "passwordReset" && loginUIState !== "passwordResetSuccess"}
        <TextInput
          autocomplete="email"
          id="login-email"
          labelText="Username or E-Mail"
          bind:value={form.email}
          invalid={errors?.email ? true : false}
          on:keydown={handleKeyDown}
        />
      {/if}
      {#if loginUIState === "login"}
        <PasswordInput
          id="login-password"
          labelText="Password"
          bind:value={form.password}
          invalid={errors?.password ? true : false}
          autocomplete="current-password"
          on:keydown={handleKeyDown}
        >
          <!-- Slot statt Standard-<p>, damit das Label wie beim TextInput aussieht -->
          <span slot="labelText">Password</span>
        </PasswordInput>
      {/if}

      {#if (errors?.email || errors?.password) && loginUIState === "login"}
        <p class="error-message">{errorMessage}</p>
      {/if}

      {#if loginUIState === "passwordResetSuccess"}
        <PasswordInput
          id="login-password-1"
          placeholder="New Password"
          bind:value={form.password}
          invalid={errors?.password ? true : false}
          description={errors?.password}
          autocomplete="new-password"
        />
        <PasswordInput
          id="login-password-2"
          placeholder="Repeat New Password"
          bind:value={form.password2}
          invalid={errors?.password2 ? true : false}
          description={errors?.password2}
          autocomplete="new-password"
        />
      {/if}
      <div class="form-actions-area">
        {#if loginUIState === "login"}
          <!--
          <p
            class="underline"
            on:click={togglePasswordReset}
            on:keydown={(e) => {
              if (e.key === "Enter") {
                togglePasswordReset();
              }
            }}
          >
            Reset Password
          </p>
          -->
        {:else}
          <Button
            variant="secondary"
            setting={true}
            iconLeft={true}
            on:click={togglePasswordReset}
            disabled={isFetching}
            text="Back"
            icon="arrow_left"
          />
        {/if}

        {#if loginUIState === "login"}
          <Button
            variant="secondary"
            setting={true}
            on:click={submitHandler}
            disabled={!form.email || !form.password}
            notAllowed={!form.email || !form.password}
            text="Sign In"
            width="100%"
          />
        {:else if loginUIState === "passwordResetSuccess"}
          <Button
            variant="secondary"
            setting={true}
            iconRight={false}
            on:click={changePasswordSubmitHandler}
            disabled={isFetching}
            text="Change Password"
          />
          <!--
        {:else if loginUIState !== "passwordReset"}
          <Button
            variant="secondary"
            setting={true}
            iconRight={false}
            on:click={passwordResetSubmitHandler}
            disabled={isFetching}
            text="Reset Password"
          />
        -->
        {/if}
      </div>
    </form>

    {#if enableSSO && loginUIState === "login"}
      <div class="separator">
        <span>or</span>
      </div>

      <div>
        <MicrosoftButton />
      </div>
    {/if}
  {:else}
    <div class="center">
      <LoadingRipple />
      <p>Loading...</p>
    </div>
  {/if}
</div>

<style>
  .login-module-container {
    box-sizing: border-box;
    width: 100%;
    padding: 0 var(--left-gap);
    margin-bottom: 5.5rem;
  }
  /* Carbon-Labels, Inputs und Buttons bringen IBM Plex mit, hier einheitlich Inter */
  .login-module-container :global(.bx--label),
  .login-module-container :global(.bx--text-input),
  .login-module-container :global(.bx--password-input),
  .login-module-container :global(button) {
    font-family: var(--font-family-inter);
  }
  h1 {
    font-family: var(--font-family-inter);
    font-size: calc(var(--n) * 1.5); /*  24px */
    font-weight: 700;
    line-height: 1.25rem;
    margin-bottom: 2rem;
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  .form-actions-area {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.5rem;
  }
  @media (max-width: 1100px) {
    .form-actions-area {
      flex-direction: column-reverse;
      align-items: flex-start;
      gap: 1rem;
    }
  }
  p {
    font-family: var(--font-family-inter);
    font-size: calc(var(--n) * 0.75); /* 12px */
    font-weight: 300;
    line-height: 1rem;
    text-align: left;
    color: var(--pax-black);
  }
  p:hover {
    cursor: pointer;
    color: var(--pax-orange);
  }
  .error-message,
  .error-message:hover {
    color: var(--pax-danger);
    text-decoration: none;
    text-align: left;
    cursor: default;
  }

  .center {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .setup_incomplete {
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 2rem;
    align-items: center;
  }
  .setup_incomplete,
  p {
    text-align: center;
  }

  :global(.login-module-container form .bx--password-input) {
    border: solid 1px var(--pax-black-inactive);
    border-radius: 0.5rem;
    height: 48px;
  }

  :global(.login-module-container form .bx--password-input::placeholder) {
    color: var(--pax-black-inactive);
  }
  :global(.login-module-container form .bx--password-input:focus) {
    outline: solid var(--bw) var(--pax-black);
    color: var(--pax-black);
  }

  .separator {
    display: flex;
    align-items: center;
    text-align: center;
    margin: 1.5rem 0;
    /*padding-top: 1rem;*/
  }

  .separator::before,
  .separator::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #ccc;
  }

  .separator span {
    padding: 0 1rem;
    color: #666;
    font-size: 0.875rem;
  }
</style>
